/* eslint-disable no-lonely-if */
import React, { useState, useEffect, Fragment } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useFormikContext } from 'formik';
import { isEmpty, get } from 'lodash';
import { useSelector } from 'react-redux';
import { windowWrapper } from '@clubmed/utils';
import {
  getHeaderContactInfoTitle,
  findAlphabetIndex,
} from 'Components/widgets/contact-info/ContactInfoUtils';
import { useAppContext } from 'Context/ReactAppContext';
import type { State as userInfoType } from 'Store/user-info/reducers/userInfoReducer';
import 'Store/user-info/reducers/userInfoReducer';
import { t } from 'Src/i18n';
import {
  getAttendeeValidationSchemaByRole,
  getAddressValidationSchema,
} from 'Store/validation-schema/validationSchema.selectors';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import * as pleUtils from 'Utils/ple-utils';
import { Button } from 'Components/frameworks/button/Button';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { Title } from 'Components/frameworks/title/Title';
import { UserInfoSummary } from 'Components/widgets/user-info-summary/UserInfoSummary';
import { PersonInfoFormContent } from './PersonInfoFormContent';
import { PersonInfoForm } from './PersonInfoForm';
import { isSchemaHasUnicodeFields } from './PersonInfoUtils';
type Props = {
  parentIndex: number;
  isUserConnected: boolean;
  arrivalDate: string;
  attendeeIdentifier: string;
  attendeeIndex: number;
  connectedUserInfo?: userInfoType;
  hasMainAttendeeErrors?: boolean;
  hasPerformValidation?: boolean;
  hasAddress: boolean;
  i18n: I18N;
  id: string;
  isChild: boolean;
  isMain: boolean;
  listIdentifier: string;
};

const CommonTitle = ({ isMain, headerTitle, id }) => {
  return (
    <div className="ml-20 mt-20">
      <Title
        id={`attendee-${id}`}
        className={classnames('air360-replay-ignore', {
          'mb-10': isMain,
        })}
        theme="title-brand-n4"
        {...{
          'data-cs-mask': true,
        }}
      >
        {headerTitle}
      </Title>
      {isMain && (
        <p className="mb-10 text-14">
          {t('booking.participants.personnalInfosHeader.mainContactDescription')}
        </p>
      )}
    </div>
  );
};

export const PersonInfo = ({
  parentIndex,
  isUserConnected,
  arrivalDate,
  attendeeIdentifier,
  attendeeIndex,
  connectedUserInfo,
  hasMainAttendeeErrors,
  hasPerformValidation,
  hasAddress,
  i18n,
  id,
  isChild,
  isMain,
  listIdentifier,
}: Props) => {
  const appContext = useAppContext();
  const { trackClickEvent } = useTrackEvent();
  const { values, errors } = useFormikContext();
  const isQuickLogged = appContext.isUserQuickLogged;
  const loginUrl = isQuickLogged
    ? pleUtils.buildStepupUrl({
        redirectUri: windowWrapper.getLocation(),
        locale: appContext.locale,
      })
    : pleUtils.buildSigninUrl({
        redirectUri: windowWrapper.getLocation(),
        locale: appContext.locale,
      });
  const isMiniaturizedLoginButton = isFeatureFlipped(appContext)(
    'participantPageConnection.miniaturizedButton',
  );
  const attendeeSchema = useSelector(getAttendeeValidationSchemaByRole(isMain, isChild));
  const addressSchema = useSelector(getAddressValidationSchema);
  const [isExpandedMode, setIsExpandedMode] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);
  const attendeeSchemaIndex = findAlphabetIndex(id);
  const isValid = !get(errors, attendeeIdentifier, false);
  const userLogged = appContext.isUserLogged;
  const displayMiniaturizedButton = !userLogged && i18n?.account?.authenticated?.greeting;
  const headerTitle = getHeaderContactInfoTitle({
    attendeeIdentifier,
    attendeeIndex,
    hasUnicodeFields: isSchemaHasUnicodeFields(attendeeSchema),
    formValues: values,
    isChild,
    isMain,
    isValid,
  });
  useEffect(() => {
    if (hasPerformValidation) {
      if (isMain) {
        setIsExpandedMode(hasMainAttendeeErrors);
      } else {
        if (isChild) {
          const isChildValid = !get(errors, attendeeIdentifier, false);
          isChildValid ? setIsExpandedMode(false) : setIsExpandedMode(true);
        }
      }
    }
  }, [hasPerformValidation]);
  const personInfoFormContent = (
    <PersonInfoFormContent
      arrivalDate={arrivalDate}
      addressSchema={addressSchema}
      attendeeSchema={attendeeSchema}
      attendeeIdentifier={attendeeIdentifier}
      connectedUserInfo={connectedUserInfo}
      hasAddress={hasAddress}
      i18n={i18n}
      id={id}
      isChild={isChild}
      isMain={isMain}
      isDisabled={isDisabled}
    />
  );
  return (
    <div data-testid={isMain ? 'main-participant-block' : 'other-participant-block'}>
      {!isExpandedMode && !isEmpty(connectedUserInfo) ? (
        <Fragment>
          <CommonTitle headerTitle={headerTitle} isMain={isMain} id={attendeeSchemaIndex} />
          <UserInfoSummary
            onClickEdit={() => {
              setIsExpandedMode(true);
              trackClickEvent(TRACK_EVENT_NAMES.CLICK_PARTICIPANTS, {
                detail_click: 'edit_main_traveler',
              });
            }}
            user={{
              ...get(values, attendeeIdentifier),
              mobilePhoneNumber: get(values, attendeeIdentifier, {}).mobile,
            }}
          />
        </Fragment>
      ) : (
        <Capsule header="none">
          <div>
            {isMiniaturizedLoginButton &&
              attendeeIndex === 0 &&
              displayMiniaturizedButton &&
              !isChild && (
                <div className="float-right pl-20 pr-20 text-center">
                  <Button
                    className="mx-auto max-w-full"
                    data-testid="loggin-button"
                    isExternalLink
                    onClick={() =>
                      trackClickEvent(TRACK_EVENT_NAMES.CLICK_PARTICIPANTS, {
                        detail_click: 'login_form_participants',
                      })
                    }
                    url={loginUrl}
                  >
                    {t('booking.participants.connectToPLE.buttonLabel')}
                  </Button>
                </div>
              )}
            <CommonTitle headerTitle={headerTitle} isMain={isMain} id={attendeeSchemaIndex} />
          </div>
          <PersonInfoForm
            parentIndex={parentIndex}
            isUserConnected={isUserConnected}
            isMain={isMain}
            isChild={isChild}
            personalInfoFormContent={personInfoFormContent}
            listIdentifier={listIdentifier}
            attendee={get(values, attendeeIdentifier, {})}
            attendeeIdentifier={attendeeIdentifier}
            setIsDisabled={setIsDisabled}
          />
        </Capsule>
      )}
      {!isEmpty(connectedUserInfo) && isExpandedMode && isMain ? (
        <div className="mt-10 flex justify-end">
          <Button
            isDisabled={!isValid}
            onClick={() => {
              setIsExpandedMode(!isExpandedMode);
              trackClickEvent(TRACK_EVENT_NAMES.CLICK_PARTICIPANTS, {
                detail_click: 'edit_main_traveler_validate',
              });
            }}
            size="medium"
          >
            {t('booking.common.validate')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
